import React, { useMemo } from 'react'

import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'

export const useApplicantsAndContactProfiles = (applicationUuid: string) => {
  const { data: applicationProfilesResponse } = useListApplicationProfilesQuery(applicationUuid, {
    skip: !applicationUuid,
  })
  const applicationProfiles = useMemo(
    () => applicationProfilesResponse?.results || [],
    [applicationProfilesResponse]
  )

  const { data: nonApplicantsResponse } = useListNonApplicantsQuery(applicationUuid, {
    skip: !applicationUuid,
  })
  const nonApplicants = useMemo(() => nonApplicantsResponse?.results || [], [nonApplicantsResponse])

  const emailIsDuplicated = (appProfile, email) => {
    /**
     * Checks if the provided email is duplicated across the lists of application profiles
     * and non-applicants, excluding the specified application profile and its married profile.
     *
     * This function searches for any profile that has the same email as the provided `email`,
     * while excluding profiles linked by marriage to the given `appProfile` or the profile itself.
     *
     * @param {Object} appProfile - The application profile to check against for exclusion criteria.
     * @param {string} email - The email address to check for duplication.
     * @returns {boolean} - Returns true if a duplicate email is found; otherwise, false.
     */
    return [...applicationProfiles, ...nonApplicants].some((profile) => {
      const isSameEmail = profile?.profile?.email === email
      const isNotMarriedProfile = profile?.id !== appProfile?.married_to_profile
      const isNotSameProfile = profile?.id !== appProfile?.id

      return isSameEmail && isNotMarriedProfile && isNotSameProfile
    })
  }

  return { emailIsDuplicated }
}
